import { useState } from "react";
import { api } from "@/lib/api";
import { CHAR_FIELDS } from "@/components/screenplay/helpers";

/**
 * Modal "✨ Sugerir contenido" para una ficha de personaje.
 *
 * Aparece desde el botón dentro de cada ficha cuando la ficha está
 * "poco desarrollada" (≥4 campos vacíos de los 10 del modelo).
 *
 * Flujo:
 *   1. Click en "✨ Sugerir contenido" → carga el modal en estado "loading"
 *   2. Llama a `POST /characters/{cid}/suggest-fill` (la IA analiza el guion)
 *   3. Muestra las sugerencias por campo + checkbox + texto editable
 *   4. Usuario revisa → "Aplicar a la ficha" → llama a PATCH del personaje
 */
const FIELD_LABEL = Object.fromEntries(CHAR_FIELDS.map((f) => [f.id, f.label]));

export default function SuggestFillModal({ open, onClose, projectId, character, onApplied }) {
  // phases: loading → review → applying → error
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [suggestions, setSuggestions] = useState({});
  const [appearance, setAppearance] = useState(0);
  // Estado editable: campos seleccionados + valor (que el usuario puede editar)
  const [picked, setPicked] = useState({});  // { fieldId: true/false }
  const [edited, setEdited] = useState({});  // { fieldId: "..." }

  const reset = () => {
    setPhase("loading");
    setError("");
    setSuggestions({});
    setAppearance(0);
    setPicked({});
    setEdited({});
  };

  // Auto-load al abrir
  const load = async () => {
    setPhase("loading");
    setError("");
    try {
      const r = await api.post(
        `/icaa/projects/${projectId}/screenplay/characters/${character.id}/suggest-fill`
      );
      const sugg = r.data?.suggestions || {};
      setSuggestions(sugg);
      setAppearance(r.data?.appearance_count || 0);
      // Por defecto, marca TODAS como activas. Usuario desmarca lo que no quiera.
      const initialPicked = Object.fromEntries(Object.keys(sugg).map((k) => [k, true]));
      setPicked(initialPicked);
      setEdited({ ...sugg });
      setPhase("review");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Error al sugerir contenido";
      setError(String(msg));
      setPhase("error");
    }
  };

  // Trigger load cuando el modal pasa a abierto + character cambia
  if (open && phase === "loading" && Object.keys(suggestions).length === 0 && !error) {
    // disparar de manera no bloqueante
    setTimeout(() => load(), 0);
  }

  const togglePicked = (f) => setPicked((p) => ({ ...p, [f]: !p[f] }));
  const updateEdited = (f, v) => setEdited((e) => ({ ...e, [f]: v }));

  const apply = async () => {
    const toApply = {};
    for (const f of Object.keys(picked)) {
      if (picked[f] && (edited[f] || "").trim()) {
        toApply[f] = edited[f].trim();
      }
    }
    if (Object.keys(toApply).length === 0) {
      reset();
      onClose?.();
      return;
    }
    setPhase("applying");
    try {
      const r = await api.patch(
        `/icaa/projects/${projectId}/screenplay/characters/${character.id}`,
        toApply
      );
      onApplied?.(r.data);
      reset();
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Error al aplicar";
      setError(String(msg));
      setPhase("review");
    }
  };

  if (!open) return null;
  const pickedCount = Object.values(picked).filter(Boolean).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={() => { if (phase !== "applying" && phase !== "loading") { reset(); onClose?.(); } }}
      data-testid="suggest-fill-modal"
    >
      <div
        className="relative flex h-[88vh] w-[760px] max-w-full flex-col overflow-hidden rounded-xl bg-[#FAFAF6] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-4 border-b border-stone-200 bg-white px-5 py-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#941620]">
              ✨ Sugerir contenido
            </p>
            <p className="mt-0.5 truncate font-serif text-base text-stone-900">
              Ficha: <strong className="font-mono uppercase">{character?.name || "—"}</strong>
            </p>
          </div>
          <button
            onClick={() => { if (phase !== "applying" && phase !== "loading") { reset(); onClose?.(); } }}
            data-testid="suggest-fill-close"
            className="rounded-full border border-stone-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-700 hover:border-[#941620]"
          >
            ✕ Cerrar
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          {phase === "loading" && (
            <div className="flex h-full flex-col items-center justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-stone-200 border-t-[#941620]" />
              <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-stone-500">Leyendo el guion…</p>
              <p className="mt-2 max-w-sm text-center text-[12px] text-stone-500">
                La IA analiza todas las líneas donde aparece {character?.name} para inferir contenido coherente
              </p>
            </div>
          )}

          {phase === "applying" && (
            <div className="flex h-full flex-col items-center justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-stone-200 border-t-[#941620]" />
              <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-stone-500">Aplicando cambios…</p>
            </div>
          )}

          {phase === "error" && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="text-[2rem]">⚠</div>
              <p className="mt-3 max-w-md text-[13px] text-rose-800">{error}</p>
              <button
                onClick={load}
                className="mt-5 rounded-full bg-[#941620] px-5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white"
              >
                Reintentar
              </button>
            </div>
          )}

          {phase === "review" && (
            <div className="space-y-3" data-testid="suggest-fill-review">
              <div className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-[11px] text-stone-700">
                <strong>{Object.keys(suggestions).length}</strong> sugerencia{Object.keys(suggestions).length === 1 ? "" : "s"} basada{Object.keys(suggestions).length === 1 ? "" : "s"} en <strong>{appearance}</strong> aparición{appearance === 1 ? "" : "es"} de {character?.name} en el guion
              </div>

              {Object.keys(suggestions).length === 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
                  La IA no ha podido inferir contenido suficiente del guion. Quizás necesita que el personaje tenga más diálogos o acciones que lo describan.
                </div>
              ) : (
                <ul className="space-y-2">
                  {Object.entries(suggestions).map(([field, value]) => (
                    <li
                      key={field}
                      className={`rounded-lg border px-3 py-2 transition ${
                        picked[field] ? "border-[#941620]/40 bg-[#941620]/5" : "border-stone-200 bg-stone-50 opacity-70"
                      }`}
                      data-testid={`suggest-fill-row-${field}`}
                    >
                      <div className="flex items-baseline gap-2">
                        <input
                          type="checkbox"
                          checked={!!picked[field]}
                          onChange={() => togglePicked(field)}
                          data-testid={`suggest-fill-toggle-${field}`}
                          className="mt-1 h-4 w-4 accent-[#941620]"
                        />
                        <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-500">
                          {FIELD_LABEL[field] || field}
                        </span>
                      </div>
                      <textarea
                        value={edited[field] || ""}
                        onChange={(e) => updateEdited(field, e.target.value)}
                        rows={Math.min(4, Math.ceil((edited[field] || "").length / 70) + 1)}
                        data-testid={`suggest-fill-text-${field}`}
                        className="mt-1.5 w-full resize-y rounded border border-stone-300 bg-white px-2 py-1.5 text-[12px] leading-relaxed text-stone-800 outline-none focus:border-[#941620]"
                      />
                      <p className="mt-1 text-[10px] italic text-stone-400">
                        Sugerencia IA original: «{value.slice(0, 90)}{value.length > 90 ? "…" : ""}»
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {phase === "review" && Object.keys(suggestions).length > 0 && (
          <footer className="border-t border-stone-200 bg-white px-5 py-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] text-stone-600">
                {pickedCount === 0
                  ? "Marca al menos una sugerencia para aplicar."
                  : `${pickedCount} campo${pickedCount === 1 ? "" : "s"} pendiente${pickedCount === 1 ? "" : "s"} de aplicar.`}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => { reset(); onClose?.(); }}
                  className="rounded-full border border-stone-300 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-700 hover:border-stone-500"
                >
                  Cancelar
                </button>
                <button
                  onClick={apply}
                  disabled={pickedCount === 0}
                  data-testid="suggest-fill-apply"
                  className="rounded-full bg-[#941620] px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-[#7a1019] disabled:cursor-not-allowed disabled:bg-stone-300"
                >
                  Aplicar a la ficha
                </button>
              </div>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}
